import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Order } from '@interfaces/OrderTypes';
import { SelfServiceMergePayload } from '@utils/orders/selfService';

const initialState: Order[] = [];

type OrderId = Pick<Order, 'id'>;

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    initOrders: (_, action: PayloadAction<Order[]>) => {
      return [...action.payload];
    },
    addOrder: (state, action: PayloadAction<Order>) => {
      // Prevenir duplicados - verificar si el pedido ya existe
      const exists = state.find((order) => order.id === action.payload.id);
      if (exists) {
        // El pedido ya existe, no agregar duplicado
        return state;
      }

      return [action.payload, ...state]; // new orders are added at the beginning of the list
    },
    updateOrder: (state, action: PayloadAction<Order>) => {
      return state.map((order) => {
        if (order.id === action.payload.id) {
          return action.payload;
        }
        return order;
      });
    },
    deleteOrder: (state, action: PayloadAction<OrderId>) => {
      return state.filter((order) => order.id !== action.payload.id);
    },
    // Primitiva de merge segura (T2): escribe SOLO `self_service` en el pedido `id`, y SOLO si el
    // read ya trae la asociación (`order_uuid` truthy) Y el del store aún no está enriquecido.
    // Nunca toca `estatus` ni ningún otro campo (preserva el estado optimista que el operador pudo
    // haber avanzado). Es seguro por la inmutabilidad de `self_service` (un pedido = un
    // `orders_order`): "mergear solo si el store está null" nunca des-agrupa una mesa ya agrupada.
    mergeSelfService: (state, action: PayloadAction<SelfServiceMergePayload>) => {
      const { id, self_service } = action.payload;
      if (!self_service?.order_uuid) return state; // incoming sin enriquecer → no-op (anti des-agrupar)
      // Si ningún pedido coincide (o el que coincide ya estaba agrupado), devolver la MISMA
      // referencia: un array nuevo sin cambios reales forzaría un re-render no-op del Kanban.
      let changed = false;
      const next = state.map((order) => {
        if (order.id !== id) return order;
        if (order.self_service?.order_uuid) return order; // ya agrupado → inmutable, no pisar
        changed = true;
        return { ...order, self_service };
      });
      return changed ? next : state;
    },
    reset: () => initialState,
  },
});
